import type {
  SearchAppContextType,
  SearchFacetMember,
  SearchResultItem,
  SearchKeyValueParam,
} from '../../types';
import {
  mergeLookupKeyValueParams,
  parseSearchParamsFromSearch,
} from './search-url';
import {
  HYDRATE_FROM_URL,
  SET_QUERY,
  SET_FACET,
  SET_ALL_FACETS,
  CLEAR_FILTERS,
  SEARCH_PENDING,
  SEARCH_SUCCESS,
  SEARCH_FAILED,
  SET_SEARCH_RUNTIME_CONFIG,
  SET_PAGE,
} from './search-app-actions';
import type { HydratedSearchUrl } from './search-url';
import {
  sanitizeFunnelbackSearchInput,
  sanitizeFunnelbackSearchQuery,
} from '../../utils/funnelback-fetch-url';
import { DEAKIN_SEARCH_PAGE_SIZE } from '../../funnelback/deakin-config';
import { resolveSearchRuntimeConfig } from './resolve-search-runtime-config';
import { resolveInitialSelectedFacets } from './resolve-initial-selected-facets';
import type { PageMetadataFilters } from './parse-page-metadata-facets';
import { normalizeSelectedFacets } from './normalize-selected-facets';

export const defaultSearchAppContext: SearchAppContextType = {
  query: '',
  selectedFacets: {},
  facetMembers: {},
  funnelbackFacets: [],
  facetExtras: null,
  questionEcho: null,
  returnCode: null,
  results: [],
  totalResults: 0,
  page: 1,
  pageSize: DEAKIN_SEARCH_PAGE_SIZE,
  status: 'idle',
  error: null,
  lookupBaseUrl: '',
  suggestBaseUrl: '',
  collectionId: '',
  lookupExtras: [
    { key: 'fmt', value: 'json++' },
    { key: 'alpha', value: '0.5' },
    { key: 'profile', value: 'default' },
  ],
  suggestExtras: [],
  pageUrlPath: '',
  showFacets: true,
  showTextSearch: true,
};

export function createInitialSearchAppState(
  locationSearch: string,
  options?: {
    pageUrlPath?: string;
    defaultQuery?: string;
    showFacets?: boolean;
    showTextSearch?: boolean;
    pageMetadataFilters?: PageMetadataFilters;
  },
): SearchAppContextType {
  const hydrated = parseSearchParamsFromSearch(locationSearch);
  const fromUrl = hydrated.query;
  const fallback = options?.defaultQuery?.trim() ?? '';
  const query = fromUrl !== '' ? fromUrl : fallback;
  const pageUrlPath = options?.pageUrlPath ?? '';
  const showTextSearch = options?.showTextSearch ?? true;
  const runtimeConfig =
    typeof document !== 'undefined'
      ? resolveSearchRuntimeConfig(pageUrlPath)
      : null;
  const selectedFacets = resolveInitialSelectedFacets(hydrated, {
    showTextSearch,
    pageMetadataFilters: options?.pageMetadataFilters,
  });

  return {
    ...defaultSearchAppContext,
    pageUrlPath,
    showFacets: options?.showFacets ?? true,
    showTextSearch,
    query,
    page: hydrated.page,
    selectedFacets,
    ...(runtimeConfig
      ? {
          lookupBaseUrl: runtimeConfig.lookupBaseUrl,
          suggestBaseUrl: runtimeConfig.suggestBaseUrl,
          collectionId: runtimeConfig.collectionId,
          ...(runtimeConfig.pageSize != null
            ? { pageSize: runtimeConfig.pageSize }
            : {}),
          lookupExtras: mergeLookupKeyValueParams(
            defaultSearchAppContext.lookupExtras,
            runtimeConfig.lookupExtras,
          ),
          suggestExtras: runtimeConfig.suggestExtras,
        }
      : {
          lookupExtras: [
            { key: 'fmt', value: 'json++' },
            { key: 'alpha', value: '0.5' },
            { key: 'profile', value: 'default' },
          ],
          suggestExtras: [
            { key: 'fmt', value: 'json++' },
            { key: 'alpha', value: '0.5' },
            { key: 'profile', value: '_default_preview' },
            { key: 'show', value: '10' },
          ],
        }),
  };
}

export type SearchAppAction =
  | { type: typeof HYDRATE_FROM_URL; payload: HydratedSearchUrl }
  | { type: typeof SET_QUERY; payload: string }
  | { type: typeof SET_FACET; payload: { facetId: string; values: string[] } }
  | { type: typeof SET_ALL_FACETS; payload: Record<string, string[]> }
  | { type: typeof SET_PAGE; payload: number }
  | { type: typeof CLEAR_FILTERS }
  | { type: typeof SEARCH_PENDING }
  | {
      type: typeof SEARCH_SUCCESS;
      payload: {
        results: SearchResultItem[];
        totalResults: number;
        facetMembers: Record<string, SearchFacetMember[]>;
        funnelbackFacets: unknown[];
        facetExtras: SearchAppContextType['facetExtras'];
        questionEcho: SearchAppContextType['questionEcho'];
        returnCode: SearchAppContextType['returnCode'];
        page?: number;
        pageSize?: number;
      };
    }
  | { type: typeof SEARCH_FAILED; payload: { error: string } }
  | {
      type: typeof SET_SEARCH_RUNTIME_CONFIG;
      payload: {
        lookupBaseUrl?: string;
        suggestBaseUrl?: string;
        collectionId?: string;
        pageSize?: number;
        lookupExtras?: SearchKeyValueParam[];
        suggestExtras?: SearchKeyValueParam[];
      };
    };

export const searchAppReducer = (
  state: SearchAppContextType,
  action: SearchAppAction,
): SearchAppContextType => {
  switch (action.type) {
    case HYDRATE_FROM_URL: {
      const { query, page, selectedFacets } = action.payload;
      return {
        ...state,
        query: sanitizeFunnelbackSearchQuery(query),
        page: page >= 1 ? page : 1,
        selectedFacets: { ...selectedFacets },
      };
    }
    case SET_QUERY:
      return {
        ...state,
        query: sanitizeFunnelbackSearchInput(action.payload),
        page: 1,
      };
    case SET_FACET: {
      const { facetId, values } = action.payload;
      const next = { ...state.selectedFacets };
      if (values.length === 0) {
        delete next[facetId];
      } else {
        next[facetId] = values;
      }
      return { ...state, selectedFacets: next, page: 1 };
    }
    case SET_ALL_FACETS: {
      const next: Record<string, string[]> = {};
      for (const [facetId, values] of Object.entries(action.payload)) {
        if (values?.length) {
          next[facetId] = values;
        }
      }
      return { ...state, selectedFacets: next, page: 1 };
    }
    case SET_PAGE:
      return { ...state, page: Math.max(1, action.payload) };
    case CLEAR_FILTERS:
      return { ...state, selectedFacets: {}, page: 1 };
    case SEARCH_PENDING:
      return {
        ...state,
        status: 'loading',
        error: null,
      };
    case SEARCH_SUCCESS: {
      const {
        results,
        totalResults,
        facetMembers,
        funnelbackFacets,
        facetExtras,
        questionEcho,
        returnCode,
        page: responsePage,
        pageSize: responsePageSize,
      } = action.payload;
      const pageSize =
        responsePageSize != null ? responsePageSize : state.pageSize;
      const maxPage = Math.max(
        1,
        Math.ceil(totalResults / Math.max(1, pageSize)),
      );
      const requestedPage = responsePage != null ? responsePage : state.page;
      const page = Math.min(Math.max(1, requestedPage), maxPage);
      const nextFacets =
        Array.isArray(funnelbackFacets) && funnelbackFacets.length > 0
          ? funnelbackFacets
          : state.funnelbackFacets;
      const normalizedSelectedFacets = normalizeSelectedFacets(
        state.selectedFacets,
        nextFacets,
      );
      return {
        ...state,
        status: 'success',
        error: null,
        results,
        totalResults,
        facetMembers,
        funnelbackFacets: nextFacets,
        facetExtras,
        questionEcho,
        returnCode,
        page,
        selectedFacets: normalizedSelectedFacets,
        ...(responsePageSize != null ? { pageSize: responsePageSize } : {}),
      };
    }
    case SEARCH_FAILED:
      return {
        ...state,
        status: 'error',
        error: action.payload.error,
        results: [],
        totalResults: 0,
        facetMembers: {},
        funnelbackFacets: [],
        facetExtras: null,
        questionEcho: null,
        returnCode: null,
      };
    case SET_SEARCH_RUNTIME_CONFIG: {
      const p = action.payload;
      return {
        ...state,
        ...(p.lookupBaseUrl != null ? { lookupBaseUrl: p.lookupBaseUrl } : {}),
        ...(p.suggestBaseUrl != null
          ? { suggestBaseUrl: p.suggestBaseUrl }
          : {}),
        ...(p.collectionId != null ? { collectionId: p.collectionId } : {}),
        ...(p.pageSize != null && Number.isFinite(p.pageSize) && p.pageSize > 0
          ? { pageSize: Math.floor(p.pageSize) }
          : {}),
        ...(p.lookupExtras != null
          ? {
              lookupExtras: mergeLookupKeyValueParams(
                state.lookupExtras,
                p.lookupExtras,
              ),
            }
          : {}),
        ...(p.suggestExtras != null ? { suggestExtras: p.suggestExtras } : {}),
      };
    }
    default:
      return state;
  }
};
